import React, { useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { UploadCloud, FileText, Loader2, ArrowRight, Sparkles, RotateCcw } from "lucide-react";
import api from "@/lib/api";

const SCORE_COLOR = (score) => (score >= 75 ? "#10B981" : score >= 50 ? "#F59E0B" : "#FB7185");
const ACCEPTED_EXT = [".pdf", ".docx", ".txt"];

export const ResumeAtsWidget = () => {
  const [state, setState] = useState("idle"); // idle | dragging | loading | success | error
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const inputRef = useRef(null);

  const runUpload = useCallback(async (file) => {
    if (!file) return;
    const ext = "." + (file.name.split(".").pop() || "").toLowerCase();
    if (!ACCEPTED_EXT.includes(ext)) {
      setErrorMsg("Please upload a .pdf, .docx, or .txt file.");
      setState("error");
      return;
    }
    setState("loading");
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post("/public/ats-preview", formData, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      setState("success");
    } catch (e) {
      setErrorMsg(e?.response?.data?.detail || "Couldn't analyze this file. Please try again.");
      setState("error");
    }
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    runUpload(e.dataTransfer.files?.[0]);
  }, [runUpload]);

  const reset = () => { setState("idle"); setResult(null); setErrorMsg(""); };

  return (
    <div className="relative" data-testid="resume-ats-widget">
      <div
        onDragOver={(e) => { e.preventDefault(); if (state === "idle") setState("dragging"); }}
        onDragLeave={() => { if (state === "dragging") setState("idle"); }}
        onDrop={onDrop}
        onClick={() => (state === "idle" || state === "error") && inputRef.current?.click()}
        data-testid="resume-dropzone"
        className={`relative rounded-3xl border-2 border-dashed p-8 min-h-[420px] flex flex-col items-center justify-center text-center overflow-hidden transition-colors duration-200 cursor-pointer
          ${state === "dragging" ? "border-[#5C9DFF] bg-[#5C9DFF]/10 -translate-y-1 shadow-[0_0_50px_rgba(92,157,255,0.25)]" : "border-white/15 bg-[#0A101D]/70 backdrop-blur-xl hover:border-white/30"}`}
      >
        <input
          ref={inputRef} type="file" accept=".pdf,.docx,.txt" className="hidden" data-testid="resume-file-input"
          onChange={(e) => runUpload(e.target.files?.[0])}
        />

        <AnimatePresence mode="wait">
          {(state === "idle" || state === "dragging") && (
            <motion.div key="idle" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="relative">
              <div className="w-16 h-16 rounded-2xl bg-[#5C9DFF]/15 border border-[#5C9DFF]/30 flex items-center justify-center mx-auto mb-5">
                <UploadCloud className="w-7 h-7 text-[#5C9DFF]" strokeWidth={1.5} />
              </div>
              <p className="text-white font-semibold text-base">Check Your Resume</p>
              <p className="text-slate-400 text-sm mt-1.5 max-w-[240px] mx-auto">Drag & drop your resume (.pdf, .docx, .txt) for an instant AI-calculated ATS score</p>
              <span className="inline-block mt-4 text-xs font-semibold text-[#5C9DFF] bg-[#5C9DFF]/10 border border-[#5C9DFF]/20 rounded-full px-4 py-1.5">or click to browse</span>
            </motion.div>
          )}

          {state === "loading" && (
            <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="resume-upload-loading">
              <Loader2 className="w-10 h-10 text-[#5C9DFF] animate-spin mx-auto mb-4" />
              <p className="text-white font-semibold text-sm">Analyzing your resume...</p>
              <p className="text-slate-500 text-xs mt-1">Scanning for ATS keywords, structure & impact</p>
            </motion.div>
          )}

          {state === "error" && (
            <motion.div key="error" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="resume-upload-error">
              <FileText className="w-10 h-10 text-rose-400 mx-auto mb-4" />
              <p className="text-rose-300 text-sm font-medium max-w-[260px] mx-auto">{errorMsg}</p>
              <button onClick={(e) => { e.stopPropagation(); reset(); }} data-testid="resume-retry-btn" className="inline-flex items-center gap-1.5 mt-4 text-xs font-semibold text-white bg-white/10 hover:bg-white/15 px-4 py-2 rounded-lg transition-colors">
                <RotateCcw className="w-3.5 h-3.5" /> Try again
              </button>
            </motion.div>
          )}

          {state === "success" && result && (
            <motion.div key="success" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} className="w-full" data-testid="ats-score-preview" onClick={(e) => e.stopPropagation()}>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-3">Your ATS Score</p>
              <div className="relative w-32 h-32 mx-auto mb-4">
                <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                  <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="8" />
                  <motion.circle
                    cx="50" cy="50" r="42" fill="none" stroke={SCORE_COLOR(result.ats_score)} strokeWidth="8" strokeLinecap="round"
                    strokeDasharray={2 * Math.PI * 42}
                    initial={{ strokeDashoffset: 2 * Math.PI * 42 }}
                    animate={{ strokeDashoffset: 2 * Math.PI * 42 * (1 - result.ats_score / 100) }}
                    transition={{ duration: 1, ease: "easeOut" }}
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-3xl font-heading font-bold text-white" data-testid="ats-score-value">{result.ats_score}</span>
                  <span className="text-[10px] text-slate-500">/ 100</span>
                </div>
              </div>
              {result.seniority_level && (
                <p className="text-center text-xs text-slate-400 mb-4">AI-detected level: <span className="text-white font-semibold">{result.seniority_level}</span></p>
              )}

              <div className="relative rounded-xl border border-white/10 bg-white/[0.03] p-4 text-left overflow-hidden">
                <p className="text-[11px] font-semibold text-emerald-400 mb-1">Strength</p>
                <p className="text-xs text-slate-300 leading-relaxed">{result.top_strength}</p>
                <p className="text-[11px] font-semibold text-amber-400 mt-3 mb-1">Top Improvement</p>
                <div className="relative">
                  <p className="text-xs text-slate-300 leading-relaxed blur-[3px] select-none" data-testid="ats-improvement-blurred">{result.top_improvement}</p>
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A101D] via-[#0A101D]/60 to-transparent" />
                </div>
              </div>

              <Link
                to="/register" data-testid="signup-hook-cta"
                className="mt-4 flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-white text-sm font-semibold px-5 py-3 rounded-xl transition-all hover:-translate-y-0.5 w-full"
              >
                <Sparkles className="w-4 h-4" /> See Your Full Breakdown <ArrowRight className="w-3.5 h-3.5" />
              </Link>
              <button onClick={reset} data-testid="ats-check-another-btn" className="mt-2.5 w-full text-center text-xs text-slate-500 hover:text-slate-300 transition-colors">Check another resume</button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
